import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TicketState } from './types';
import {
  fetchTickets,
  fetchTicketById,
  createTicket,
  createTicketOperator,
  updateTicket,
  solveTicket,
  closeTicket,
  assignTicket,
  addTicketMessage,
  addTicketMessageWithFiles,
} from './thunks';

const initialState: TicketState = {
  tickets: [],
  totalCount: 0,
  currentTicket: null,
  status: 'idle',
  listStatus: 'idle',
  error: null,
};

const ticketSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    resetTickets(state) {
      state.tickets = [];
      state.totalCount = 0;
      state.listStatus = 'idle';
    },
    resetCurrentTicket(state) {
      state.currentTicket = null;
      state.status = 'idle';
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    /* ── fetchTickets ── */
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.listStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.tickets = action.payload.results;
        state.totalCount = action.payload.count;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.error = action.payload ?? 'Неизвестная ошибка';
      });

    /* ── fetchTicketById ── */
    builder
      .addCase(fetchTicketById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTicketById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentTicket = action.payload;
      })
      .addCase(fetchTicketById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Неизвестная ошибка';
      });

    /* ── createTicket / createTicketOperator ── */
    builder
      .addCase(createTicket.fulfilled, (state, action) => {
        state.tickets.unshift({ ...action.payload, sender_email: '' });
        state.totalCount += 1;
      })
      .addCase(createTicketOperator.fulfilled, (state, action) => {
        state.tickets.unshift({ ...action.payload, sender_email: '' });
        state.totalCount += 1;
      });

    /* ── updateTicket / solveTicket / closeTicket / assignTicket ── */
    const applyUpdate = (state: TicketState, action: PayloadAction<any>) => {
      const updated = action.payload;
      if (state.currentTicket?.id === updated.id) {
        state.currentTicket = updated;
      }
      const idx = state.tickets.findIndex((t) => t.id === updated.id);
      if (idx !== -1) {
        state.tickets[idx] = { ...state.tickets[idx], ...updated };
      }
    };
    builder.addCase(updateTicket.fulfilled, applyUpdate);
    builder.addCase(solveTicket.fulfilled, applyUpdate);
    builder.addCase(closeTicket.fulfilled, applyUpdate);
    builder.addCase(assignTicket.fulfilled, applyUpdate);

    /* ── addTicketMessage / withFiles ── */
    const appendMessage = (state: TicketState, action: PayloadAction<any>) => {
      if (state.currentTicket) {
        state.currentTicket.messages.push(action.payload);
      }
    };
    builder.addCase(addTicketMessage.fulfilled, appendMessage);
    builder.addCase(addTicketMessageWithFiles.fulfilled, appendMessage);
  },
});

export const { resetTickets, resetCurrentTicket, clearError } =
  ticketSlice.actions;
export default ticketSlice.reducer;
